import {
  DefaultButton,
  DialogFooter,
  Link,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import { REPO_URL } from "../../Shared/shared.consts";
import { ExperimentType } from "../../Types";
import { useMemo } from "react";
import useAppInsights from "../../Hooks/UseAppInsights";
import { NavType } from "../../Shared/AppInsights";

interface ArchiveExperimentConfirmFinalProps {
  /**
   * The experiment to be archived.
   */
  experiment: ExperimentType;
  /**
   * When true, shows loading state for archiving process.
   */
  isLoading: boolean;
  /**
   * Callback function on successful archiving of the experiment.
   */
  onArchive: () => void;
  /**
   * Callback function to cancel the archiving process.
   */
  onCancel: () => void;
}

/**
 * Final confirmation dialog component to archive an experiment. Will make a pull request
 * to the ev2 experimentation repo to archive the experiment configuration.
 */
const ArchiveExperimentConfirmFinal: React.FC<
  ArchiveExperimentConfirmFinalProps
> = ({ experiment, isLoading, onArchive, onCancel }) => {
  const { trackNavigationEvent } = useAppInsights(
    "ArchiveExperimentConfirmationFinal",
  );

  const configFileUrl = useMemo(
    () => `${REPO_URL}?path=${experiment.__fullpath}`,
    [experiment.__fullpath],
  );

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text block>
        This will create a pull request (which will need to be approved and
        merged within ADO) to{" "}
        <Link
          href={REPO_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open AzureUX Experimentation repository"
          onClick={() => {
            trackNavigationEvent({
              destination: REPO_URL,
              navType: NavType.External,
            });
          }}
        >
          AzureUX Experimentation
        </Link>{" "}
        to archive the experiment{" "}
        <Link
          href={configFileUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackNavigationEvent({
              destination: configFileUrl,
              navType: NavType.External,
            });
          }}
        >
          configuration file
        </Link>
        .
      </Text>
      <MessageBar messageBarType={MessageBarType.warning}>
        You will no longer be able to manage the experiment while archived. Are
        you sure you want to archive?
      </MessageBar>
      <DialogFooter>
        <PrimaryButton
          text="Yes, archive"
          onClick={onArchive}
          disabled={isLoading}
          data-testid="confirm-archive-button"
        />
        <DefaultButton
          text="No, cancel"
          onClick={onCancel}
          disabled={isLoading}
          data-testid="cancel-archive-button"
        />
      </DialogFooter>
    </Stack>
  );
};

export default ArchiveExperimentConfirmFinal;
